import { useEffect, useState } from 'react';
import { useMsal } from 'src/auth/firebaseMsalCompat';

import { loginRequest } from '../config/authConfig';
import { useFirebaseAuth } from '../auth/FirebaseAuthContext';
import {
  getSubscriptionStatus,
  subscribeSubscriptionStatus,
} from '../services/subscriptionStatus';
import {
  resolveEntitlement,
  type Entitlement,
  type SubscriptionStatus,
} from './useSubscriptionStatus';

/**
 * Whether the caller can spend a credit right now. Mirrors the backend
 * consume path (`creditBalance` first, then `extraCredits`) — a
 * subscriber who has used up the month's allotment is *not* treated as
 * having credits, because their next generation would be demo-limited.
 */
export const hasSpendableCreditsFromStatus = (
  status: Pick<SubscriptionStatus, 'creditBalance' | 'extraCredits'> | null
): boolean => (status?.creditBalance ?? 0) + (status?.extraCredits ?? 0) >= 1;

/**
 * Side-effect-free credit read for the project-page reminder bar.
 *
 * Shares the deduplicated `getSubscriptionStatus` cache (NavBar already
 * primes it on every authenticated page) and follows later refreshes via
 * `subscribeSubscriptionStatus`. Unlike `useSubscriptionStatus` this
 * never fulfills a Stripe checkout or rewrites the URL — that has no
 * business running on a project page.
 *
 * When `enabled` is false (guest / sample / signed-out) no request is
 * made; `entitlement` stays `pending` and `hasSpendableCredits` is false
 * so callers must treat that path themselves (hide the upgrade bar on
 * guests and samples — guests already see GuestSaveBanner).
 */
export const useCreditAvailability = (
  enabled: boolean
): { hasSpendableCredits: boolean; entitlement: Entitlement } => {
  const { instance, inProgress } = useMsal();
  const { user } = useFirebaseAuth();
  const uid = enabled && user && !user.isAnonymous ? user.uid : null;
  const [status, setStatus] = useState<SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (inProgress !== 'none' || !enabled || !uid) {
      setStatus(null);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setStatus(null);
    setLoading(true);
    setError(null);

    const unsubscribe = subscribeSubscriptionStatus((data, dataUid) => {
      if (cancelled) return;
      if (dataUid !== uid) {
        setStatus(null);
        return;
      }
      setStatus(data);
      setLoading(false);
      setError(null);
    });

    (async () => {
      try {
        const authResult = await instance.acquireTokenSilent({
          ...loginRequest,
        });
        const data = await getSubscriptionStatus(authResult.idToken, uid);
        if (!cancelled) {
          setStatus(data);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [instance, inProgress, enabled, uid]);

  const entitlement: Entitlement = enabled
    ? resolveEntitlement(status, loading, error)
    : 'pending';

  return {
    hasSpendableCredits:
      entitlement === 'known' && hasSpendableCreditsFromStatus(status),
    entitlement,
  };
};
